import { appConfirm } from "@/lib/appDialog";
import { cloneElement, isValidElement, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, OPP_STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import AgreementAlert from "@/components/AgreementAlert";
import { toast } from "sonner";
import { Loader2, Eye, MapPin, Clock, Users, BookOpen, Plus, X, Save, Edit3, AlertTriangle, ShieldCheck, ClipboardCheck, Trash2, Upload } from "lucide-react";

const STATUS_STYLES = {
  draft:        "bg-slate-100 text-slate-700 border-slate-200",
  under_review: "bg-amber-50 text-amber-800 border-amber-200",
  pending_contract: "bg-rose-50 text-rose-800 border-rose-200",
  needs_edit:   "bg-orange-50 text-orange-800 border-orange-200",
  published:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed:       "bg-slate-100 text-slate-700 border-slate-200",
  archived:     "bg-slate-50 text-slate-500 border-slate-200",
  rejected:     "bg-rose-50 text-rose-700 border-rose-200",
};

const EMPTY_OPPORTUNITY = {
  title: "", description: "", program_name: "", city: "", category: "",
  seats: 5, duration_weeks: 12, total_training_hours: "", cover_image_url: "", tags: [],
  requirements: [], knowledge_reqs: [], skills_reqs: [], behaviors_reqs: [],
  outcomes: [], training_start: "", admin_notes: "",
  delivery_mode: "onsite",
};

function readDraft(key) {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.form && typeof parsed.form === "object" ? parsed.form : null;
  } catch { return null; }
}

function writeDraft(key, form) {
  if (!key) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify({ version: 1, saved_at: new Date().toISOString(), form }));
    return true;
  } catch { return false; }
}

function removeDraft(key) {
  if (!key) return;
  try { window.localStorage.removeItem(key); } catch { /* storage may be blocked */ }
}

export default function AssociationOpportunities() {
  const { user } = useAuth();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const draftKey = user?.id ? `emad_association_opportunity_draft_v1:${user.id}` : null;

  const openCreate = () => {
    const restored = readDraft(draftKey);
    setForm({ ...EMPTY_OPPORTUNITY, ...(restored || {}) });
    if (restored) toast.info("تمت استعادة مسودة الفرصة السابقة");
  };

  const openEdit = (opportunity) => {
    const editKey = draftKey ? draftKey + ":edit:" + opportunity.id : null;
    const restored = readDraft(editKey);
    setForm({ ...opportunity, ...(restored || {}), id: opportunity.id });
    if (restored) toast.info("تمت استعادة تعديلات الفرصة المحفوظة");
  };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/opportunities/manage");
      setOpps(data.opportunities || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const groups = {
    pending_contract: opps.filter((o) => o.status === "pending_contract"),
    under_review: opps.filter((o) => o.status === "under_review"),
    needs_edit:   opps.filter((o) => o.status === "needs_edit"),
    published:    opps.filter((o) => o.status === "published"),
    rejected:     opps.filter((o) => o.status === "rejected"),
    other:        opps.filter((o) => !["pending_contract","under_review","needs_edit","published","rejected"].includes(o.status)),
  };

  return (
    <DashboardLayout>
      <div data-testid="association-opportunities">
        <div className="mb-8 flex flex-wrap justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">فرص التدريب التعاوني</div>
            <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">الفرص المرتبطة بجمعيتك</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              أنشئ فرصك التدريبية بنفسك. تُراجعها إدارة المنصّة قبل نشرها، ويصلك إشعار بالنتيجة (قبول / رفض / بحاجة لتعديل).
            </p>
          </div>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 h-fit" data-testid="new-opp-btn">
            <Plus className="h-5 w-5" /> فرصة جديدة
          </button>
        </div>

        <AgreementAlert />

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="assoc-opps-error" />
        ) : opps.length === 0 ? (
          <EmptyState icon={BookOpen} title="لا توجد فرص بعد" description="أنشئ أول فرصة تدريب — ستذهب لإدارة المنصّة للمراجعة." testid="assoc-opps-empty" />
        ) : (
          <div className="space-y-8">
            {groups.pending_contract.length > 0 && <Group title="بانتظار اتفاقية التعاون" hint="أكمل الاتفاقية ووقّعها لتنتقل هذه الفرص لمراجعة الإدارة" icon={AlertTriangle} tint="text-rose-700">
              {groups.pending_contract.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}
            </Group>}
            {groups.under_review.length > 0 && <Group title="تحت المراجعة" hint="بانتظار مراجعة إدارة المنصّة" icon={ClipboardCheck} tint="text-amber-800">
              {groups.under_review.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}
            </Group>}
            {groups.needs_edit.length > 0 && <Group title="بحاجة لتعديل" hint="الإدارة طلبت تحسينات — عدّلها لإعادة المراجعة" icon={AlertTriangle} tint="text-orange-700">
              {groups.needs_edit.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}
            </Group>}
            {groups.published.length > 0 && <Group title="منشورة (نشطة)" hint="متاحة لتقديم الطلاب والتسكين المباشر" icon={ShieldCheck} tint="text-emerald-700">
              {groups.published.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}
            </Group>}
            {groups.rejected.length > 0 && <Group title="مرفوضة" hint="الإدارة اعتذرت عن هذه الفرصة" tint="text-rose-700">
              {groups.rejected.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}
            </Group>}
            {groups.other.length > 0 && <Group title="أخرى">{groups.other.map((o) => <OppCard key={o.id} o={o} onEdit={() => openEdit(o)} onDeleted={load} />)}</Group>}
          </div>
        )}

        {form && <OppForm form={form} setForm={setForm} draftKey={draftKey} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      </div>
    </DashboardLayout>
  );
}

function Group({ title, hint, icon: Icon, tint = "text-slate-700", children }) {
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {Icon && <Icon className={`h-4 w-4 ${tint}`} />}
        <h2 className={`font-heading font-black text-lg ${tint}`}>{title}</h2>
        {hint && <span className="text-xs text-slate-500 w-full sm:w-auto">— {hint}</span>}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function OppCard({ o, onEdit, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const linked = (o.applications_count || 0) + (o.placements_count || 0);
  const ownsOpportunity = o.can_manage !== false;
  const canDelete = ownsOpportunity && linked === 0 && o.status !== "archived";
  const canEdit = ownsOpportunity && o.status !== "archived";
  const handleDelete = async () => {
    const msg = linked > 0
      ? `لا يمكن حذف هذه الفرصة — يوجد ${linked} طلب/تسكين مرتبط بها. عليك أوّلاً معالجة الطلبات.`
      : `تأكيد حذف الفرصة "${o.title}"؟ لا يمكن التراجع.`;
    if (linked > 0) { toast.error(msg); return; }
    if (!await appConfirm(msg)) return;
    setDeleting(true);
    try {
      await api.delete(`/opportunities/${o.id}`);
      toast.success("تم حذف الفرصة");
      onDeleted && onDeleted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };
  return (
    <div className="rounded-2xl border border-cream-200 bg-white overflow-hidden card-shadow" data-testid={`assoc-opp-${o.id}`}>
      <div className="h-24 bg-gradient-to-br from-teal-700 to-teal-900 relative">
        {o.cover_image_url && <img src={o.cover_image_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />}
        <div className="absolute top-3 end-3">
          <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-bold border ${STATUS_STYLES[o.status] || STATUS_STYLES.draft}`}>{OPP_STATUS_LABELS[o.status] || o.status}</span>
        </div>
        {o.category && <div className="absolute bottom-3 start-3 inline-flex px-2.5 py-1 rounded-full bg-white/95 text-teal-800 text-[11px] font-bold">{o.category}</div>}
      </div>
      <div className="p-4">
        <h3 className="font-heading font-bold text-slate-900 leading-tight line-clamp-2 min-h-[3rem]">{o.title}</h3>
        {o.status === "pending_contract" && (
          <Link to={o.agreement_document_id ? `/dashboard/contracts?document=${o.agreement_document_id}` : "/dashboard/contracts"} className="mt-2 flex items-center justify-center min-h-11 rounded-xl bg-rose-700 text-white text-sm font-bold hover:bg-rose-600" data-testid={`assoc-opp-agreement-${o.id}`}>
            أكمل اتفاقية التعاون
          </Link>
        )}
        {o.program_name && <div className="mt-1 text-xs text-teal-700 font-medium">{o.program_name}</div>}
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
          {o.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{o.city}</span>}
          <span className="inline-flex items-center gap-1 arabic-numerals"><Clock className="h-3.5 w-3.5" />{o.duration_weeks} أسبوع</span>
          {o.total_training_hours ? <span className="inline-flex items-center gap-1 arabic-numerals"><Clock className="h-3.5 w-3.5" />{o.total_training_hours} ساعة تدريبية</span> : null}
          <span className="inline-flex items-center gap-1 arabic-numerals"><Users className="h-3.5 w-3.5" />{o.seats_filled || 0}/{o.seats}</span>
        </div>
        {linked > 0 && (
          <div className="mt-2 text-[11px] text-slate-500 arabic-numerals">
            مرتبط بـ {linked} طلب/تسكين
          </div>
        )}
        {o.moderation_note && (
          <div className="mt-3 p-2.5 rounded-lg bg-cream-50 border border-cream-200 text-xs">
            <div className="font-bold text-slate-700 mb-0.5">ملاحظة الإدارة</div>
            <div className="text-slate-700 whitespace-pre-line">{o.moderation_note}</div>
          </div>
        )}
        <div className="mt-4 flex items-center gap-2">
          <Link to={`/opportunities/${o.id}`} target="_blank" className="inline-flex items-center gap-1.5 text-sm text-teal-700 font-semibold hover:underline" data-testid={`view-assoc-opp-${o.id}`}>
            <Eye className="h-4 w-4" /> عرض
          </Link>
          <div className="ms-auto flex items-center gap-1.5">
            {canEdit && onEdit && (
              <button onClick={onEdit} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-800 text-xs font-bold hover:bg-teal-100" data-testid={`edit-opp-${o.id}`} title={o.status === "published" ? "التعديل سيعيد الفرصة للمراجعة" : "تعديل"}>
                <Edit3 className="h-3.5 w-3.5" /> تعديل
              </button>
            )}
            {ownsOpportunity && <button
              onClick={handleDelete}
              disabled={deleting || !canDelete}
              title={!canDelete ? "لا يمكن الحذف بوجود طلبات/تسكينات" : "حذف الفرصة"}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`delete-opp-${o.id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} حذف
            </button>}
          </div>
        </div>
        {o.status === "published" && (
          <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1">
            ملاحظة: أي تعديل على الفرصة يعيدها لمراجعة الإدارة قبل إعادة النشر.
          </div>
        )}
      </div>
    </div>
  );
}

function OppForm({ form, setForm, draftKey, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const isEdit = !!form.id;
  const activeDraftKey = draftKey ? (isEdit ? draftKey + ":edit:" + form.id : draftKey) : null;
  useEffect(() => {
    if (!activeDraftKey) return undefined;
    const timer = window.setTimeout(() => {
      try {
        const savedAt = new Date().toISOString();
        if (writeDraft(activeDraftKey, form)) setDraftSavedAt(savedAt);
      } catch { /* storage unavailable must not interrupt entry */ }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [activeDraftKey, form]);
  const closeAndKeepDraft = () => {
    if (activeDraftKey && !writeDraft(activeDraftKey, form)) {
      toast.error("تعذر حفظ المسودة على هذا الجهاز؛ أبقِ النموذج مفتوحًا أو أرسل الفرصة الآن");
      return;
    }
    onClose();
    toast.info(isEdit ? "حُفظت التعديلات ويمكنك إكمالها لاحقًا" : "حُفظت المسودة ويمكنك إكمالها لاحقًا");
  };
  const discardDraft = async () => {
    if (!await appConfirm(isEdit ? "هل تريد حذف التعديلات المحفوظة والعودة للنسخة المنشورة؟" : "هل تريد حذف المسودة نهائيًا؟ ستفقد جميع البيانات التي أدخلتها.")) return;
    removeDraft(activeDraftKey)
    onClose();
    toast.success(isEdit ? "تم حذف التعديلات المحفوظة" : "تم حذف المسودة");
  };
  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("اختر صورة صالحة"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("context", "opportunity_image");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, cover_image_url: "/api/files/" + data.file_id });
      toast.success("تم رفع صورة الفرصة");
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.title?.trim() || !form.description?.trim()) {
      toast.error("عنوان الفرصة ووصفها مطلوبان"); return;
    }
    if (!form.training_start) { toast.error("تاريخ بداية التدريب مطلوب"); return; }
    const seats = Number(form.seats);
    const durationWeeks = Number(form.duration_weeks);
    const totalTrainingHours = Number(form.total_training_hours);
    if (!Number.isInteger(seats) || seats < 1 || seats > 200) { toast.error("عدد المقاعد يجب أن يكون بين 1 و200"); return; }
    if (!Number.isInteger(durationWeeks) || durationWeeks < 1 || durationWeeks > 48) { toast.error("مدة التدريب يجب أن تكون بين أسبوع و48 أسبوعًا"); return; }
    if (!Number.isInteger(totalTrainingHours) || totalTrainingHours < 1 || totalTrainingHours > 5000) { toast.error("أدخل إجمالي ساعات صحيحًا بين 1 و5000"); return; }
    if ((form.delivery_mode || "onsite") !== "remote" && !form.city?.trim()) { toast.error("المدينة مطلوبة للفرص الحضورية والهجينة"); return; }
    setSaving(true);
    try {
      // Auto-compute training_end from training_start + duration_weeks
      const weeks = durationWeeks;
      let computedEnd = null;
      if (form.training_start) {
        const start = new Date(form.training_start);
        if (!isNaN(start)) {
          const end = new Date(start);
          end.setDate(end.getDate() + weeks * 7);
          computedEnd = end.toISOString().slice(0, 10);
        }
      }
      const body = {
        title: form.title, description: form.description, program_name: (form.program_name || "").trim(),
        city: form.city || null, category: form.category || null,
        seats, duration_weeks: weeks,
        total_training_hours: totalTrainingHours,
        cover_image_url: form.cover_image_url || null,
        delivery_mode: form.delivery_mode || "onsite",
        tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map((x) => x.trim()).filter(Boolean),
        requirements: Array.isArray(form.requirements) ? form.requirements : String(form.requirements || "").split("\n").map((x) => x.trim()).filter(Boolean),
        knowledge_reqs: Array.isArray(form.knowledge_reqs) ? form.knowledge_reqs : String(form.knowledge_reqs || "").split("\n").map((x) => x.trim()).filter(Boolean),
        skills_reqs: Array.isArray(form.skills_reqs) ? form.skills_reqs : String(form.skills_reqs || "").split("\n").map((x) => x.trim()).filter(Boolean),
        behaviors_reqs: Array.isArray(form.behaviors_reqs) ? form.behaviors_reqs : String(form.behaviors_reqs || "").split("\n").map((x) => x.trim()).filter(Boolean),
        outcomes: Array.isArray(form.outcomes) ? form.outcomes : String(form.outcomes || "").split("\n").map((x) => x.trim()).filter(Boolean),
        training_start: form.training_start || null, training_end: computedEnd,
        admin_notes: (form.admin_notes || "").trim() || null,
      };
      if (isEdit) await api.patch(`/opportunities/${form.id}`, body);
      else await api.post("/opportunities", body);
      toast.success(isEdit ? "تم إرسال التعديل للمراجعة" : "تم إرسال الفرصة لإدارة المنصّة للمراجعة");
      removeDraft(activeDraftKey);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="opportunity-form-backdrop">
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="opp-form-modal">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
          <div>
            <h3 className="font-heading font-bold text-slate-900 text-lg">{isEdit ? "تعديل الفرصة" : "إنشاء فرصة التدريب التعاوني"}</h3>
            <div className="text-xs text-slate-500 mt-0.5">سترسَل لإدارة المنصّة لمراجعتها قبل النشر.</div>
            <div className="mt-1 text-[11px] font-medium text-emerald-700" data-testid="opportunity-draft-status">{draftSavedAt ? (isEdit ? "التعديلات محفوظة تلقائيًا" : "المسودة محفوظة تلقائيًا") : "سيتم الحفظ تلقائيًا"}</div>
          </div>
          <button type="button" onClick={closeAndKeepDraft} className="min-h-11 min-w-11 rounded-lg hover:bg-slate-100 grid place-items-center" aria-label={isEdit ? "إغلاق وحفظ التعديلات" : "إغلاق وحفظ المسودة"}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {isEdit && form.moderation_note && <div className="rounded-xl bg-orange-50 border border-orange-200 p-4"><div className="font-bold text-orange-800 mb-2">التعديلات المطلوبة من الإدارة</div><div className="text-sm text-slate-700 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">{form.moderation_note}</div></div>}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="opp-training-dates-top">
            <F label="بداية التدريب"><input type="date" value={(form.training_start || "").slice(0,10)} onChange={(e) => setForm({ ...form, training_start: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3" data-testid="opp-training-start" /></F>
            <F label="نهاية التدريب (تُحسب تلقائياً)">
              <input
                type="text"
                readOnly
                value={form.training_start ? (() => {
                  const w = Number(form.duration_weeks) || 12;
                  const s = new Date(form.training_start);
                  if (isNaN(s)) return "";
                  const e = new Date(s); e.setDate(e.getDate() + w * 7);
                  return e.toISOString().slice(0, 10);
                })() : ""}
                placeholder="اختر بداية التدريب والمدة"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 arabic-numerals cursor-not-allowed"
                data-testid="opp-training-end-preview-top"
              />
            </F>
          </div>
          <F label="عنوان الفرصة التدريبية"><input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-title" /></F>
          <F label="اسم البرنامج"><input value={form.program_name || ""} onChange={(e) => setForm({ ...form, program_name: e.target.value })} className="w-full rounded-lg border-2 border-teal-300 bg-teal-50/40 px-4 py-3 text-base font-black text-slate-900 focus:border-teal-600 focus:ring-4 focus:ring-teal-600/10 outline-none" data-testid="opp-program" placeholder="مثال: برنامج تنمية القدرات الإدارية" /></F>
          <F label="الوصف"><textarea rows={4} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-desc" /></F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label={form.delivery_mode === "remote" ? "المدينة (غير مطلوبة عن بُعد)" : "المدينة"}><input value={form.city || ""} disabled={form.delivery_mode === "remote"} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm disabled:bg-slate-100 disabled:text-slate-400" data-testid="opp-city" /></F>
            <F label="التصنيف"><input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="تقنية، صحة، تعليم..." className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-cat" /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="عدد المقاعد"><input type="number" min={1} max={200} value={form.seats || 5} onChange={(e) => setForm({ ...form, seats: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm arabic-numerals" data-testid="opp-seats" /></F>
            <F label="مدة الفرصة"><select value={form.duration_weeks || 12} onChange={(e) => setForm({ ...form, duration_weeks: Number(e.target.value) })} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm" data-testid="opp-weeks">{Array.from({ length: 48 }, (_, index) => index + 1).map((week) => <option key={week} value={week}>{week} أسبوع</option>)}</select></F>
          </div>
          <F label="إجمالي عدد ساعات الفرصة التدريبية"><input type="number" min={1} max={5000} value={form.total_training_hours || ""} onChange={(e) => setForm({ ...form, total_training_hours: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm arabic-numerals" data-testid="opp-total-hours" /></F>
          <F label="نوع الفرصة">
            <select
              value={form.delivery_mode || "onsite"}
              onChange={(e) => setForm({ ...form, delivery_mode: e.target.value, city: e.target.value === "remote" ? "" : form.city })}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm"
              data-testid="opp-delivery-mode"
            >
              <option value="onsite">حضوري</option>
              <option value="remote">عن بُعد</option>
              <option value="hybrid">هجين</option>
            </select>
          </F>
          <F label="صورة غلاف الفرصة">
            <div className="rounded-xl border border-dashed border-slate-300 p-3">
              {form.cover_image_url && <img src={form.cover_image_url} alt="" className="h-32 w-full object-cover rounded-lg mb-3" />}
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-50 text-teal-800 font-bold cursor-pointer">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع صورة
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => uploadCover(e.target.files?.[0])} data-testid="opp-image-upload" />
              </label>
            </div>
          </F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="بداية التدريب"><input type="date" value={(form.training_start || "").slice(0,10)} onChange={(e) => setForm({ ...form, training_start: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3" data-testid="opp-training-start-bottom" /></F>
            <F label="نهاية التدريب (تُحسب تلقائياً)">
              <input
                type="text"
                readOnly
                value={form.training_start ? (() => {
                  const w = Number(form.duration_weeks) || 12;
                  const s = new Date(form.training_start);
                  if (isNaN(s)) return "";
                  const e = new Date(s); e.setDate(e.getDate() + w * 7);
                  return e.toISOString().slice(0, 10);
                })() : ""}
                placeholder="اختر بداية التدريب والمدة"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 arabic-numerals cursor-not-allowed"
                data-testid="opp-training-end-preview"
              />
            </F>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 space-y-3" data-testid="opp-competencies">
            <div className="text-xs font-bold text-teal-700 uppercase tracking-widest">الجدارات المطلوبة</div>
            <F label="المعارف المطلوبة (كل بند في سطر)"><textarea rows={3} value={Array.isArray(form.knowledge_reqs) ? form.knowledge_reqs.join("\n") : (form.knowledge_reqs || "")} onChange={(e) => setForm({ ...form, knowledge_reqs: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-knowledge-reqs" /></F>
            <F label="المهارات المطلوبة (كل بند في سطر)"><textarea rows={3} value={Array.isArray(form.skills_reqs) ? form.skills_reqs.join("\n") : (form.skills_reqs || "")} onChange={(e) => setForm({ ...form, skills_reqs: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-skills-reqs" /></F>
            <F label="السلوكيات المطلوبة (كل بند في سطر)"><textarea rows={3} value={Array.isArray(form.behaviors_reqs) ? form.behaviors_reqs.join("\n") : (form.behaviors_reqs || "")} onChange={(e) => setForm({ ...form, behaviors_reqs: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm" data-testid="opp-behaviors-reqs" /></F>
          </div>
          <F label="المهام (ما هي أهم المهام المطلوبة من الطالب؟)"><textarea rows={4} value={Array.isArray(form.outcomes) ? form.outcomes.join("\n") : (form.outcomes || "")} onChange={(e) => setForm({ ...form, outcomes: e.target.value })} className="w-full rounded-lg border border-slate-300 px-4 py-3" /></F>
          <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-4">
            <F label="ملاحظات لمدير النظام (اختياري — لن تظهر للطلاب)">
              <textarea
                rows={3}
                value={form.admin_notes || ""}
                onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
                placeholder="اكتب أي تفاصيل تريد أن يطّلع عليها مدير النظام عند المراجعة (خصوصية الجمعية، ملاحظات تنسيقية، ...)"
                className="w-full rounded-lg border border-amber-300 bg-white px-4 py-3 text-sm"
                data-testid="opp-admin-notes"
              />
            </F>
            <p className="mt-2 text-xs text-amber-800">
              هذه الملاحظة تُعرض فقط لمدير النظام في «إدارة الفرص التدريبية».
            </p>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white px-4 py-3 border-t border-slate-100 flex gap-2 justify-end">
          <button type="button" onClick={discardDraft} className="min-h-11 px-4 rounded-lg text-rose-700 hover:bg-rose-50" data-testid="discard-opportunity-draft">{isEdit ? "حذف التعديلات" : "حذف المسودة"}</button>
          <button type="button" onClick={closeAndKeepDraft} className="min-h-11 px-4 rounded-lg text-slate-700 hover:bg-slate-100">{isEdit ? "إلغاء" : "حفظ والخروج"}</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="save-opp">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isEdit ? "حفظ وإعادة المراجعة" : "إرسال للمراجعة"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  const generatedId = useId();
  const isControl = isValidElement(children) && ["input", "textarea", "select"].includes(children.type);
  if (!isControl) return (<div><div className="block text-xs font-semibold text-slate-700 mb-1">{label}</div>{children}</div>);
  const controlId = children.props.id || generatedId;
  return (<div><label htmlFor={controlId} className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>{cloneElement(children, { id: controlId })}</div>);
}
